import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Store, Search, Filter, ShoppingCart, Loader2, Sparkles } from 'lucide-react';
import { apiRequest, formatPrice } from '../lib/utils';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import SEOHead, { PAGE_SEO } from '../components/SEOHead';

// Fonction pour vérifier si un produit est nouveau (moins de 30 jours)
const isNewProduct = (createdAt) => {
    if (!createdAt) return false;
    const productDate = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now - productDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30;
};

export default function ShopPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();
    const { addToCart } = useCart();
    
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [addingToCart, setAddingToCart] = useState(null);

    const selectedCategory = searchParams.get('category') || '';

    useEffect(() => {
        fetchProducts();
        fetchCategories();
    }, [selectedCategory]);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const endpoint = selectedCategory ? `/products?category=${selectedCategory}` : '/products';
            const data = await apiRequest(endpoint);
            setProducts(data);
        } catch (error) {
            toast.error(t('shop.loadingError'));
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const data = await apiRequest('/products/categories');
            setCategories(data);
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    };

    const handleCategoryChange = (category) => {
        if (category) {
            setSearchParams({ category });
        } else {
            setSearchParams({});
        }
    };

    const handleAddToCart = async (productId) => {
        if (!user) {
            toast.error(t('shop.loginToAddCart'));
            navigate('/auth');
            return;
        }
        setAddingToCart(productId);
        try {
            await addToCart(productId);
            toast.success(t('shop.productAdded'));
        } catch (error) {
            toast.error(error.message);
        } finally {
            setAddingToCart(null);
        }
    };

    const filteredProducts = products.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="min-h-screen pt-20 pb-12" data-testid="shop-page">
            {/* SEO for Shop Page */}
            <SEOHead 
                title={PAGE_SEO.shop.title}
                description={PAGE_SEO.shop.description}
                keywords={PAGE_SEO.shop.keywords}
                canonicalUrl="https://www.ocapiscine.fr/shop"
            />
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <Store className="h-10 w-10 text-primary" />
                        <h1 className="font-heading text-3xl sm:text-4xl font-bold">{t('shop.title')}</h1>
                    </div>
                    <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                        {t('shop.subtitle')}
                    </p>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                    {/* Search */}
                    <div className="relative flex-1">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input
                            placeholder={t('common.searchProduct')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-12 h-12 rounded-full"
                            data-testid="search-input"
                        />
                    </div>

                    {/* Category filters */}
                    <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0">
                        <Button
                            variant={!selectedCategory ? 'default' : 'secondary'}
                            className="rounded-full whitespace-nowrap"
                            onClick={() => handleCategoryChange('')}
                            data-testid="category-all"
                        >
                            <Filter className="h-4 w-4 mr-2" />
                            {t('common.all')}
                        </Button>
                        {categories.map(cat => (
                            <Button
                                key={cat.id}
                                variant={selectedCategory === cat.id ? 'default' : 'secondary'}
                                className="rounded-full whitespace-nowrap"
                                onClick={() => handleCategoryChange(cat.id)}
                                data-testid={`category-${cat.id}`}
                            >
                                {cat.name}
                            </Button>
                        ))}
                    </div>
                </div>

                {/* Products Grid */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <div className="text-center py-20">
                        <Store className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
                        <h3 className="font-heading text-xl font-semibold mb-2">{t('shop.noProductFound')}</h3>
                        <p className="text-muted-foreground">{t('shop.tryAnotherSearch')}</p>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="products-grid">
                        {filteredProducts.map(product => (
                            <Card 
                                key={product.product_id} 
                                className="product-card group"
                                data-testid={`product-${product.product_id}`}
                            >
                                <div className="relative aspect-square overflow-hidden rounded-t-3xl bg-muted">
                                    <img
                                        src={product.image_url}
                                        alt={product.name}
                                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                    />
                                    <Badge className="absolute top-4 left-4 bg-background/80 backdrop-blur text-foreground">
                                        {product.brand}
                                    </Badge>
                                    
                                    {/* Badge Nouveau pour les produits récents */}
                                    {isNewProduct(product.created_at) && (
                                        <Badge 
                                            className="absolute top-4 right-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-lg animate-pulse"
                                            data-testid={`new-badge-${product.product_id}`}
                                        >
                                            <Sparkles className="h-3 w-3 mr-1" />
                                            {t('shop.new')}
                                        </Badge>
                                    )}
                                    
                                    {/* Quick add button - appears on hover */}
                                    <div className="absolute bottom-4 left-4 right-4 quick-add">
                                        <Button
                                            className="w-full rounded-full"
                                            onClick={() => handleAddToCart(product.product_id)}
                                            disabled={addingToCart === product.product_id}
                                            data-testid={`quick-add-${product.product_id}`}
                                        >
                                            {addingToCart === product.product_id ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <>
                                                    <ShoppingCart className="h-4 w-4 mr-2" />
                                                    {t('shop.add')}
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                                
                                <CardContent className="p-5">
                                    <Link to={`/shop/${product.product_id}`}>
                                        <h3 className="font-heading font-semibold mb-1 hover:text-primary transition-colors">
                                            {product.name}
                                        </h3>
                                    </Link>
                                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                                        {product.description}
                                    </p>
                                    {product.dosage_info && (
                                        <p className="text-xs font-mono bg-primary/10 text-primary px-2 py-1 rounded-full inline-block mb-3">
                                            {product.dosage_info}
                                        </p>
                                    )}
                                    <div className="flex items-center justify-between">
                                        <span className="font-heading text-xl font-bold text-primary">
                                            {formatPrice(product.price)}
                                        </span>
                                        <span className={`text-xs ${product.stock > 10 ? 'text-emerald-500' : product.stock > 0 ? 'text-amber-500' : 'text-red-500'}`}>
                                            {product.stock > 10 ? t('common.inStock') : product.stock > 0 ? `${product.stock} ${t('common.remaining')}` : t('common.outOfStock')}
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
